import { useState } from 'react';
import { DollarSign, TrendingUp, CalendarRange, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  useFinopsOverviewSummary,
  useFinopsOverviewTrend,
  useFinopsOverviewVendors,
  useFinopsOverviewCategories,
} from '@/hooks/useFinops';
import { StatCard, LoaderBlock, DeltaBadge, fmtUSD, fmtPct, fmtMonth } from './shared';
import { AlertsPanel } from './AlertsPanel';

const LATEST = 'latest';

/**
 * Overview — invoiced truth from the "Tech & Product - Tools" Ramp card.
 * Card scoping lives in the backend helper, so every number here is already
 * narrower than the (unscoped) AI & Subscriptions page.
 */
export function FinopsOverviewTab() {
  const { t } = useTranslation();
  const [month, setMonth] = useState<string>(LATEST);
  const selected = month === LATEST ? undefined : month;

  const { data: summary, isLoading } = useFinopsOverviewSummary(selected);
  const { data: trend = [] } = useFinopsOverviewTrend(12);
  const { data: vendors = [] } = useFinopsOverviewVendors(selected);
  const { data: categories = [] } = useFinopsOverviewCategories(selected);

  if (isLoading) return <LoaderBlock />;

  // Latest first in the picker; the backend already returns the trend ascending.
  const monthOptions = [...trend].map((p) => p.month).sort().reverse();
  const period = summary?.period;

  return (
    <div className="space-y-4">
      <AlertsPanel />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('finops.overview.scope_note')}</p>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 text-sm w-44">
            <SelectValue placeholder={t('finops.overview.month_label')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={LATEST}>{t('finops.overview.latest_month')}</SelectItem>
            {monthOptions.map((m) => (
              <SelectItem key={m} value={m}>{fmtMonth(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={DollarSign}
          tone="blue"
          label={t('finops.overview.stat_month_total')}
          value={fmtUSD(summary?.monthTotal)}
          sub={period ? fmtMonth(period) : undefined}
        />
        <StatCard
          icon={TrendingUp}
          tone={summary?.momDelta != null && summary.momDelta >= 0 ? 'rose' : 'green'}
          label={t('finops.overview.stat_mom')}
          value={summary?.momDelta == null ? '—' : `${summary.momDelta >= 0 ? '+' : ''}${(summary.momDelta * 100).toFixed(1)}%`}
          sub={t('finops.overview.stat_mom_sub')}
        />
        <StatCard
          icon={CalendarRange}
          tone="purple"
          label={t('finops.overview.stat_trailing12')}
          value={fmtUSD(summary?.trailing12Total)}
          sub={t('finops.overview.stat_trailing12_sub')}
        />
        <StatCard
          icon={Crown}
          tone="amber"
          label={t('finops.overview.stat_top_vendor')}
          value={summary?.topVendor?.vendor ?? '—'}
          sub={summary?.topVendor ? fmtUSD(summary.topVendor.cost) : undefined}
        />
      </div>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('finops.overview.trend_title')}</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={trend} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: any) => fmtUSD(v)} labelFormatter={(m: any) => fmtMonth(String(m))} />
            <Bar dataKey="total" fill="#1CC9B9" name="Total" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">
          {t('finops.overview.vendors_title')}
          {period && <span className="text-muted-foreground font-normal"> · {fmtMonth(period)}</span>}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3">{t('finops.overview.vendors_col_vendor')}</th>
                <th className="pr-3">{t('finops.overview.vendors_col_category')}</th>
                <th className="pr-3 text-right">{t('finops.overview.vendors_col_cost')}</th>
                <th className="pr-3 text-right">{t('finops.overview.vendors_col_prior')}</th>
                <th className="pr-3 text-right">{t('finops.overview.vendors_col_delta')}</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.vendor} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium">{v.vendor}</td>
                  <td className="pr-3 text-muted-foreground">{v.category ?? '—'}</td>
                  <td className="pr-3 text-right">{fmtUSD(v.cost)}</td>
                  <td className="pr-3 text-right text-muted-foreground">{fmtUSD(v.priorCost)}</td>
                  <td className="pr-3 text-right"><DeltaBadge value={v.momDelta} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('finops.overview.categories_title')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3">{t('finops.overview.categories_col_category')}</th>
                <th className="pr-3 text-right">{t('finops.overview.categories_col_cost')}</th>
                <th className="pr-3 text-right w-24">{t('finops.overview.categories_col_share')}</th>
                <th className="w-1/3" />
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.category} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium">{c.category}</td>
                  <td className="pr-3 text-right">{fmtUSD(c.cost)}</td>
                  <td className="pr-3 text-right text-muted-foreground">{fmtPct(c.share * 100)}</td>
                  <td className="py-2">
                    <div className="h-2 rounded bg-muted overflow-hidden">
                      <div
                        className="h-full rounded bg-brand-green"
                        style={{ width: `${Math.min(100, c.share * 100)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
